import LogManager from "../../utils/logManager";
import { callRouterLambda } from "../../utils/routerLambdaUtils";

/**
 * Parses the `displayName|email` wire format into card-shaped objects, for the degraded list the
 * panel shows when /whos-who fails.
 *
 * @param {string} externalInvitees - Comma-separated `displayName|email` entries.
 * @returns {Array<Object>} Card-shaped objects with external: true.
 */
export const parseExternalInvitees = (externalInvitees) =>
  (externalInvitees || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const pipe = entry.indexOf("|");
      const displayName = pipe > 0 ? entry.slice(0, pipe).trim() : "";
      const email = (pipe >= 0 ? entry.slice(pipe + 1) : entry).trim();
      return { displayName, email };
    })
    .filter(({ email }) => email)
    .map(({ displayName, email }) => {
      const name = displayName || email;
      return { id: email, name, email, external: true, initials: name.slice(0, 2).toUpperCase() };
    });

/**
 * Calls the /whos-who endpoint to fetch invitee profile cards.
 *
 * Internal and external invitees travel separately and in different formats, matching the split
 * the availability handler makes by email domain.
 *
 * @param {Object} params
 * @param {string} params.meetingUuid - Meeting UUID from the meeting session.
 * @param {string} params.attendees - Comma-separated aliases of the internal (Amazon) invitees
 * @param {string} params.externalInvitees - Comma-separated `displayName|email` entries for
 *   non-Amazon invitees.
 * @returns {Promise<Object>} Parsed response with { invitees: [...] }
 * @throws {Error} On network failure, non-200 response, or an in-band error payload.
 */
export const fetchWhosWho = async ({ meetingUuid, attendees, externalInvitees }) => {
  try {
    const payload = await callRouterLambda("/whos-who", { meetingUuid, attendees, externalInvitees });

    if (payload?.error) {
      throw new Error(`/whos-who returned error: ${payload.error}`);
    }

    return payload;
  } catch (error) {
    LogManager.error("Error calling /whos-who:", error);
    throw error;
  }
};
